import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RealTimeBidActivity = ({ onBidAction }) => {
  const [activeBids, setActiveBids] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Initialize with mock active bids
    const mockBids = [
      {
        id: 'AI-2024-0818-001',
        leadType: 'Auto Insurance',
        state: 'CA',
        age: 32,
        currentBid: 45.50,
        yourBid: 43.25,
        timeRemaining: 45,
        status: 'active',
        bidders: 7,
        quality: 9.2
      },
      {
        id: 'HO-2024-0818-002',
        leadType: 'Home Insurance',
        state: 'TX',
        age: 45,
        currentBid: 38.25,
        yourBid: 35.00,
        timeRemaining: 23,
        status: 'outbid',
        bidders: 5,
        quality: 8.5
      },
      {
        id: 'LI-2024-0818-003',
        leadType: 'Life Insurance',
        state: 'FL',
        age: 38,
        currentBid: 62.75,
        yourBid: 62.75,
        timeRemaining: 67,
        status: 'winning',
        bidders: 3,
        quality: 9.0
      }
    ];
    setActiveBids(mockBids);

    // Simulate real-time updates
    const interval = setInterval(() => {
      setActiveBids(prev => prev?.map(bid => ({
        ...bid,
        timeRemaining: Math.max(0, bid?.timeRemaining - 1),
        currentBid: bid?.status === 'active' && Math.random() > 0.8 
          ? bid?.currentBid + 0.25 
          : bid?.currentBid
      }))?.filter(bid => bid?.timeRemaining > 0));

      // Add random notifications
      if (Math.random() > 0.7) {
        const notificationTypes = ['bid_won', 'outbid', 'new_lead', 'budget_alert'];
        const type = notificationTypes?.[Math.floor(Math.random() * notificationTypes?.length)];
        
        const newNotification = {
          id: Date.now(),
          type,
          message: getNotificationMessage(type),
          timestamp: new Date(),
          read: false
        };

        setNotifications(prev => [newNotification, ...prev?.slice(0, 4)]);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getNotificationMessage = (type) => {
    switch (type) {
      case 'bid_won':
        return 'Congratulations! You won lead AI-2024-0818-004 for $47.25';
      case 'outbid':
        return 'You have been outbid on HO-2024-0818-005. Current bid: $41.50';
      case 'new_lead':
        return 'New high-quality lead available: Auto Insurance, CA, Age 29';
      case 'budget_alert':
        return 'Daily budget 80% consumed. $500 remaining.';
      default:
        return 'New activity in your bidding dashboard';
    }
  };

  const handleQuickBid = (bidId, amount) => {
    setActiveBids(prev => prev?.map(bid => 
      bid?.id === bidId 
        ? { ...bid, yourBid: amount, status: amount > bid?.currentBid ? 'winning' : 'active' }
        : bid
    ));
    onBidAction('quick_bid', { bidId, amount });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'winning':
        return 'text-success bg-success/10 border-success/20';
      case 'outbid':
        return 'text-error bg-error/10 border-error/20';
      case 'active':
        return 'text-accent bg-accent/10 border-accent/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'bid_won':
        return 'Trophy';
      case 'outbid':
        return 'TrendingDown';
      case 'new_lead':
        return 'Plus';
      case 'budget_alert':
        return 'AlertTriangle';
      default:
        return 'Bell';
    }
  };

  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Real-time Notifications */}
      <div className="bg-card border border-border rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={18} className="text-accent" />
            <h3 className="font-semibold text-foreground">Live Notifications</h3>
            <div className="w-2 h-2 bg-success rounded-full pulse-live" />
          </div>
          <Button variant="ghost" size="sm" iconName="Settings" />
        </div>
        
        <div className="max-h-48 overflow-y-auto">
          {notifications?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Bell" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No new notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications?.map((notification) => (
                <div key={notification?.id} className="p-3 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start space-x-3">
                    <Icon 
                      name={getNotificationIcon(notification?.type)} 
                      size={16} 
                      className="text-accent mt-0.5" 
                    />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{notification?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {notification?.timestamp?.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Active Bids */}
      <div className="bg-card border border-border rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Zap" size={18} className="text-warning" />
            <h3 className="font-semibold text-foreground">Active Bids</h3>
            <span className="px-2 py-0.5 bg-warning/10 text-warning text-xs rounded-full">
              {activeBids?.length} active
            </span>
          </div>
          <Button variant="outline" size="sm" iconName="RefreshCw" />
        </div>

        <div className="divide-y divide-border">
          {activeBids?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Zap" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No active bids</p>
              <Button variant="outline" size="sm" className="mt-2">
                Browse Available Leads
              </Button>
            </div>
          ) : (
            activeBids?.map((bid) => (
              <div key={bid?.id} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`px-2 py-1 rounded border text-xs font-medium ${getStatusColor(bid?.status)}`}>
                      {bid?.status?.toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{bid?.id}</h4>
                      <p className="text-sm text-muted-foreground">
                        {bid?.leadType} • {bid?.state} • Age {bid?.age}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-foreground">
                      {formatTimeRemaining(bid?.timeRemaining)}
                    </div>
                    <div className="text-xs text-muted-foreground">remaining</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">${bid?.currentBid}</div>
                    <div className="text-xs text-muted-foreground">Current Bid</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-accent">${bid?.yourBid}</div>
                    <div className="text-xs text-muted-foreground">Your Bid</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{bid?.bidders}</div>
                    <div className="text-xs text-muted-foreground">Bidders</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-success">{bid?.quality}</div>
                    <div className="text-xs text-muted-foreground">Quality</div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickBid(bid?.id, bid?.currentBid + 0.25)}
                      disabled={bid?.timeRemaining < 5}
                    >
                      +$0.25
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleQuickBid(bid?.id, bid?.currentBid + 1.00)}
                      disabled={bid?.timeRemaining < 5}
                    >
                      +$1.00
                    </Button>
                  </div>
                </div>

                {/* Progress bar for time remaining */}
                <div className="w-full bg-muted rounded-full h-1">
                  <div 
                    className="bg-accent h-1 rounded-full transition-all duration-1000"
                    style={{ width: `${(bid?.timeRemaining / 120) * 100}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RealTimeBidActivity;